import React from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import {motion} from 'framer-motion';
import waveBottom from './../../assets/bottom_wave_modal.png';
import waveTop from './../../assets/top_wave_modal.png';

const backgrounds = {
    blue: 'radial-gradient(circle, #4d47bf 0%, #4d33bd 20%, #3b28ce 100%)',
    lightBlue: 'radial-gradient(circle, #478ebf 0%, #339EBD 20%, #89F9F1 100%)'
}

const ModalOverlay = styled(motion.div)`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const ModalContent = styled(motion.div)<{type: 'blue' | 'lightBlue'}>`
    background: ${({type}) => backgrounds[type]};
    padding: 20px;
    border-radius: 10px;
    position: relative;
    width: 300px;
    height: 300px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

    @media (min-width: 768px) {
        width: 450px;
        height: 450px;
    }

    @media (min-width: 1024px) {
        width: 600px;
        height: 600px;
    }
`;

const modalVariants = {
    hidden: {opacity: 0, y: '-50%'},
    visible: {
        opacity: 1, y: "0",
        transition: {
            duration: 0.4
        }
    },
    exit: {opacity: 0, y: '-50%'},
};

const ChildrenModal = styled(motion.div)`
    height: 100%;
`;

const childrenVariants = {
    hidden: {opacity: 1},
    visible: {
        opacity: 1,
        transition: {
            delay: 0.5
        }
    },
}

const overlayVariants = {
    hidden: {opacity: 0},
    visible: {
        opacity: 1,
        transition: {
            duration: 0.5
        }
    },
    exit: {opacity: 0},
};


const BottomWaveImage = styled.img`
    position: absolute;
    bottom: -4px;
    right: 0;
    height: 20%;
    width: 70%;
    filter: drop-shadow(2px 4px 6px black);

    -webkit-user-select: none; /* Safari */
    -webkit-touch-callout: none; /* iOS Safari */
    -webkit-tap-highlight-color: transparent;

    pointer-events: none; /* Prevent any interactions */
    user-drag: none; /* Disable image dragging */
    -webkit-user-drag: none; /* Disable image dragging for Safari */
`;

const TopWaveImage = styled.img`
    position: absolute;
    top: -5px;
    left: -25px;
    height: 20%;
    width: 70%;
    transform: rotate(180deg);
    filter: drop-shadow(2px 4px 6px black);

    -webkit-user-select: none; /* Safari */
    -webkit-touch-callout: none; /* iOS Safari */
    -webkit-tap-highlight-color: transparent;

    pointer-events: none; /* Prevent any interactions */
    user-drag: none; /* Disable image dragging */
    -webkit-user-drag: none; /* Disable image dragging for Safari */
`;

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    type?: 'blue' | 'lightBlue';
}

export const Modal: React.FC<ModalProps> = ({isOpen, onClose, children, type = 'lightBlue'}) => {
    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <ModalOverlay
            initial="hidden"
            animate="visible"
            exit="exit"
            variants={overlayVariants}
            onClick={onClose}
        >
            <ModalContent
                type={type}
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                onClick={(e) => e.stopPropagation()}
            >
                <ChildrenModal variants={childrenVariants} initial='hidden' animate='visible' exit='exit'>
                    {children}
                </ChildrenModal>
                <TopWaveImage src={waveTop} />
                <BottomWaveImage src={waveBottom} />
            </ModalContent>
        </ModalOverlay>,
        document.getElementById('root') as HTMLElement
    );
};